import type { CampaignStatus } from '~/types/graphql';

import { SORT_DEFAULT } from '~/constants/campaign';
import { FILTER_KEY } from '~/presentation/common';
import { EPersistKey } from '~/types/persist';
import { useCallback, useState } from 'react';
import { usePersistedState } from '~/hooks';
import { ICampaignType } from '../types';

export const useInitializeCampaignFilter = () => {
  const [queryValue, setQueryValue] = useState<string | undefined>(undefined);
  const [campaignType, setCampaignType] = usePersistedState<ICampaignType>({
    key: FILTER_KEY.CAMPAIGN_TYPE,
    defaultValue: ICampaignType.TEMPLATE,
    persistKey: EPersistKey.GENERAL,
  });
  const [campaignStatus, setCampaignStatus] = usePersistedState<CampaignStatus[] | undefined>({
    key: FILTER_KEY.STATUS,
    defaultValue: [],
    extractValue: (value) => (JSON.parse(value) as CampaignStatus[]) || [],
    persistKey: EPersistKey.GENERAL,
  });
  const [sortSelected, setSortSelected] = usePersistedState<string[]>({
    key: FILTER_KEY.SORT,
    defaultValue: SORT_DEFAULT,
    extractValue: JSON.parse,
    persistKey: EPersistKey.GENERAL,
  });

  const handleCampaignTypeChange = useCallback(
    (value: ICampaignType) => setCampaignType(value),
    [setCampaignType],
  );
  const handleCampaignStatusChange = useCallback(
    (value?: CampaignStatus[]) => setCampaignStatus(value),
    [setCampaignStatus],
  );
  const handleFiltersQueryChange = useCallback((value: string) => setQueryValue(value), []);
  const handleQueryValueRemove = useCallback(() => setQueryValue(undefined), []);
  const handleFiltersClearAll = useCallback(() => {
    handleQueryValueRemove();
  }, [handleQueryValueRemove]);
  const handleSortChange = useCallback(
    (value: string[]) => {
      setSortSelected(value ? (value as string[]) : undefined);
    },
    [setSortSelected],
  );

  return {
    queryValue,
    campaignType,
    campaignStatus,
    sortSelected,
    handleSortChange,
    handleCampaignStatusChange,
    handleFiltersClearAll,
    handleFiltersQueryChange,
    handleCampaignTypeChange,
    handleQueryValueRemove,
  };
};

export type IInitializeCampaignFilter = ReturnType<typeof useInitializeCampaignFilter>;
